import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  ExternalLink,
  Eye,
  IndianRupee,
  Info,
  Loader2,
  MoreVertical,
  RefreshCw,
  Truck,
  User,
  Wallet,
} from 'lucide-react';
import {
  fetchVendorManualRefundCases,
  fetchVendorManualRefundCaseById,
  patchVendorManualRefundCase,
  VendorManualRefundCaseRow,
  VendorManualRefundRefundMath,
} from '../../services/MasterAdmin/vendorManualRefundCaseService';
import { VENDOR_ROLE_FILTER_OPTIONS, VENDOR_ROLE_NAMES } from '../../models/MasterAdmin/vendorRoleNames';

const formatInr = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const roundMoney = (n: number) => Math.round(n * 100) / 100;

/** Parse percentage input; empty → null */
function parsePctInput(s: string): number | null {
  const t = String(s).trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return n;
}

function penaltyLabel(serviceType?: string | null, short = false): string {
  if (serviceType === 'AMBULANCE') {
    return short ? 'Agency penalty' : 'Ambulance agency penalty';
  }
  return short ? 'Vendor penalty' : 'Vendor penalty';
}

function settlementInfoText(serviceType?: string | null): string {
  const pen = penaltyLabel(serviceType);
  return `Enter ${pen.toLowerCase()} as % of gross paid or edit ₹ beside it (they stay in sync when gross is known). User refund is gross minus Vedika share from the record; vendor share is fixed at ₹0 for manual refund completion.`;
}

const maOrderLedgerPath = (serviceRefId: string, serviceType: string) => {
  const params = new URLSearchParams({
    serviceType,
    returnTo: 'manual-refund-cases',
  });
  return `/master-admin/operations/order-ledger/${encodeURIComponent(serviceRefId)}?${params.toString()}`;
};

function isFullUserRefundPresetRow(
  row: { grossPaidAmount: unknown; userRefundAmount?: unknown; vedikaShareAmount?: unknown; vendorShareAmount?: unknown },
): boolean {
  const g = Number(row.grossPaidAmount);
  if (!Number.isFinite(g) || g <= 0) return false;
  const u = Number(row.userRefundAmount);
  const ve = Number(row.vedikaShareAmount ?? 0);
  const vn = Number(row.vendorShareAmount ?? 0);
  if (!Number.isFinite(u)) return false;
  return Math.abs(u - g) < 0.02 && Math.abs(ve) < 0.02 && Math.abs(vn) < 0.02;
}

const userProfilePath = (userId: string) => `/users/${encodeURIComponent(userId)}`;

const ambulanceBookingPath = (vendorId: string, serviceRefId: string) =>
  `/vendors/ambulance/${encodeURIComponent(vendorId)}/booking/${encodeURIComponent(serviceRefId)}`;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_REVIEW', label: 'In review' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
];

function caseStatusBadgeClass(status: string): string {
  switch (String(status || '').toUpperCase()) {
    case 'PENDING':
      return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800';
    case 'IN_REVIEW':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800';
    case 'COMPLETED':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800';
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
  }
}

function CaseStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${caseStatusBadgeClass(status)}`}
    >
      {String(status || '').replace(/_/g, ' ')}
    </span>
  );
}

type SettlementAmountCardProps = {
  label: string;
  sublabel: string;
  icon: React.ComponentType<{ className?: string }>;
  border: string;
  bg: string;
  accent: string;
  inputRing: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

const SettlementAmountCard: React.FC<SettlementAmountCardProps> = ({
  label,
  sublabel,
  icon: Icon,
  border,
  bg,
  accent,
  children,
  footer,
}) => (
  <div className={`flex flex-col rounded-xl border p-4 sm:p-5 ${border} ${bg}`}>
    <div className="flex items-start gap-3">
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/80 shadow-sm dark:bg-gray-900/50 ${accent}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${accent}`}>{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{sublabel}</p>
      </div>
    </div>
    <div className="mt-4">{children}</div>
    {footer ? <div className="mt-3 border-t border-gray-200/80 pt-3 dark:border-gray-700">{footer}</div> : null}
  </div>
);

const SERVICE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All services' },
  { value: 'AMBULANCE', label: 'Ambulance' },
  { value: 'MEDICINE', label: 'Medicine' },
  { value: 'HOSPITAL', label: 'Hospital' },
  { value: 'DOCTOR_INSTANT', label: 'Doctor (instant)' },
  { value: 'DOCTOR_SCHEDULED', label: 'Doctor (scheduled)' },
  { value: 'LAB_TEST', label: 'Lab test' },
  { value: 'BLOOD_BANK', label: 'Blood bank' },
  { value: 'PRODUCT', label: 'Product' },
  { value: 'MEMBERSHIP', label: 'Membership' },
];

type DetailModalProps = {
  caseId: string;
  onClose: () => void;
  onSaved: () => void;
};

const RefundMathPanel: React.FC<{
  math: VendorManualRefundRefundMath | undefined;
  serviceType?: string;
  caseStatus?: string;
}> = ({ math, serviceType, caseStatus }) => {
  if (!math) return null;
  const penLabel = penaltyLabel(serviceType);
  const isClosed = caseStatus === 'COMPLETED' || caseStatus === 'REJECTED';
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 text-sm space-y-1">
      <p className="font-semibold text-gray-900 dark:text-white">Validation ({penLabel.toLowerCase()} on gross paid)</p>
      <p className="text-gray-600 dark:text-gray-300">
        Gross paid: <span className="font-medium text-gray-900 dark:text-white">{formatInr(math.grossPaidAmount)}</span>
      </p>
      <p className="text-gray-600 dark:text-gray-300">
        Split sum: {math.splitSum != null ? formatInr(math.splitSum) : '—'}{' '}
        {math.splitOk === false && (
          <span className="text-red-600 dark:text-red-400 ml-1">(must equal gross)</span>
        )}
        {math.splitOk === true && <span className="text-emerald-600 dark:text-emerald-400 ml-1">✓</span>}
      </p>
      <p className="text-gray-600 dark:text-gray-300">
        {penLabel} vs gross: {math.penaltyOk === false ? <span className="text-red-600">Invalid range</span> : 'OK'}
      </p>
      {math.vendorShareAmount != null && math.vendorShareAmount > 0 && (
        <p className="text-gray-600 dark:text-gray-300">
          {penLabel} ≤ vendor share:{' '}
          {math.penaltyVendorOk ? (
            <span className="text-emerald-600 dark:text-emerald-400">✓</span>
          ) : (
            <span className="text-red-600 dark:text-red-400">{math.penaltyVendorMessage}</span>
          )}
        </p>
      )}
      {!isClosed && (
        <p className="text-gray-600 dark:text-gray-300">
          Ready to complete:{' '}
          {math.completionReady ? (
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">Yes</span>
          ) : (
            <span className="text-amber-600 dark:text-amber-400 font-medium">No — adjust amounts</span>
          )}
        </p>
      )}
    </div>
  );
};

const CaseDetailModal: React.FC<DetailModalProps> = ({ caseId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [row, setRow] = useState<
    | (VendorManualRefundCaseRow & {
        user: { userId: string; name?: string; phone_number?: string } | null;
        vendor: { vendorId: string; email?: string } | null;
      })
    | null
  >(null);

  const [penaltyAmount, setPenaltyAmount] = useState('');
  const [penaltyPct, setPenaltyPct] = useState('');
  const [userRefundAmount, setUserRefundAmount] = useState('');
  const [vedikaShareAmount, setVedikaShareAmount] = useState('');
  const [penaltyPctError, setPenaltyPctError] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [settlementInfoOpen, setSettlementInfoOpen] = useState(false);
  const settlementInfoRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchVendorManualRefundCaseById(caseId);
      if (!res.success || !res.data) throw new Error('Failed to load case');
      const d = res.data;
      setRow(d);
      const g = Number(d.grossPaidAmount);
      setPenaltyAmount(d.penaltyAmount != null ? String(d.penaltyAmount) : '');
      if (g > 0 && d.penaltyAmount != null && Number.isFinite(Number(d.penaltyAmount))) {
        setPenaltyPct(String(roundMoney((Number(d.penaltyAmount) / g) * 100)));
      } else {
        setPenaltyPct('');
      }
      setVedikaShareAmount(d.vedikaShareAmount != null ? String(d.vedikaShareAmount) : '');
      const ve = Number(d.vedikaShareAmount ?? 0);
      const veOk = Number.isFinite(ve) ? ve : 0;
      if (Number.isFinite(g) && g > 0) {
        setUserRefundAmount(String(roundMoney(g - veOk)));
      } else {
        setUserRefundAmount(d.userRefundAmount != null ? String(d.userRefundAmount) : '');
      }
      setPenaltyPctError(null);
      setAdminNote(d.adminNote || '');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!settlementInfoOpen) return;
    const onDown = (e: MouseEvent) => {
      if (settlementInfoRef.current && !settlementInfoRef.current.contains(e.target as Node)) {
        setSettlementInfoOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [settlementInfoOpen]);

  const gross = row ? Number(row.grossPaidAmount) : 0;
  const penLabel = row ? penaltyLabel(row.serviceType) : 'Vendor penalty';
  const penLabelShort = row ? penaltyLabel(row.serviceType, true) : 'Penalty';
  const suggestedPenalty =
    row?.metadata && typeof row.metadata === 'object' && 'suggestedPenaltyFromTenPercentOfGross' in row.metadata
      ? Number((row.metadata as { suggestedPenaltyFromTenPercentOfGross?: number }).suggestedPenaltyFromTenPercentOfGross)
      : null;

  const userPctDisplay =
    gross > 0 && userRefundAmount !== '' && Number.isFinite(Number(userRefundAmount))
      ? roundMoney((Number(userRefundAmount) / gross) * 100)
      : null;
  const penaltyAmtDisplay =
    penaltyAmount !== '' && Number.isFinite(Number(penaltyAmount)) ? Number(penaltyAmount) : null;
  const penaltyPctDisplay =
    penaltyPct.trim() !== '' && parsePctInput(penaltyPct) != null ? parsePctInput(penaltyPct) : null;
  const vedikaAmt = vedikaShareAmount !== '' && Number.isFinite(Number(vedikaShareAmount)) ? Number(vedikaShareAmount) : 0;
  const userAmt =
    userRefundAmount !== '' && Number.isFinite(Number(userRefundAmount)) ? Number(userRefundAmount) : null;
  const splitSum =
    userAmt != null && Number.isFinite(vedikaAmt) ? roundMoney(userAmt + vedikaAmt + 0) : null;
  const splitOk = gross > 0 && splitSum != null && Math.abs(splitSum - gross) < 0.02;
  const onPenaltyPctChange = (raw: string) => {
    setPenaltyPct(raw);
    const p = parsePctInput(raw);
    if (raw.trim() === '' || p === null) {
      setPenaltyPctError(null);
      setPenaltyAmount('');
      return;
    }
    if (p < 0 || p > 100) {
      setPenaltyPctError(`${penLabelShort} % must be between 0 and 100.`);
      return;
    }
    setPenaltyPctError(null);
    if (gross > 0) {
      setPenaltyAmount(String(roundMoney((p / 100) * gross)));
    }
  };

  const onPenaltyAmountChange = (raw: string) => {
    setPenaltyAmount(raw);
    if (raw.trim() === '') {
      setPenaltyPct('');
      setPenaltyPctError(null);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      setPenaltyPct('');
      setPenaltyPctError(null);
      return;
    }
    if (gross > 0) {
      const clamped = Math.max(0, Math.min(n, gross));
      if (clamped !== n) {
        setPenaltyPctError(`${penLabelShort} cannot exceed gross paid.`);
        setPenaltyAmount(String(roundMoney(clamped)));
      } else {
        setPenaltyPctError(null);
        setPenaltyAmount(String(roundMoney(clamped)));
      }
      setPenaltyPct(String(roundMoney((clamped / gross) * 100)));
    } else {
      setPenaltyPctError(null);
    }
  };

  const applyFullUserRefund = async () => {
    if (!row || row.status === 'COMPLETED' || row.status === 'REJECTED') return;
    setSaving(true);
    setError(null);
    try {
      const p = penaltyAmount === '' ? suggestedPenalty ?? 0 : Number(penaltyAmount);
      const res = await patchVendorManualRefundCase(row.caseId, {
        applyPreset: 'FULL_USER_REFUND',
        penaltyAmount: Number.isFinite(p) ? p : 0,
        status: 'COMPLETED',
      });
      if (!res.success || !res.data) throw new Error('Apply preset failed');
      await load();
      onSaved();
      onClose();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : 'Save failed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const saveFields = async () => {
    if (!row || row.status === 'COMPLETED' || row.status === 'REJECTED') return;
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (penaltyAmount !== '') body.penaltyAmount = Number(penaltyAmount);
      if (userRefundAmount !== '') body.userRefundAmount = Number(userRefundAmount);
      if (vedikaShareAmount !== '') body.vedikaShareAmount = Number(vedikaShareAmount);
      body.vendorShareAmount = 0;
      if (adminNote !== '') body.adminNote = adminNote;
      const res = await patchVendorManualRefundCase(row.caseId, body);
      if (!res.success || !res.data) throw new Error('Save failed');
      await load();
      onSaved();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : 'Save failed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const markCompleted = async () => {
    if (!row) return;
    if (penaltyPctError) {
      setError('Fix percentage validation errors before completing.');
      return;
    }
    const g = Number(row.grossPaidAmount);
    const ve = vedikaShareAmount.trim() === '' ? 0 : Number(vedikaShareAmount);
    const vn = 0;
    const u = userRefundAmount.trim() === '' ? NaN : Number(userRefundAmount);
    if (!Number.isFinite(ve) || !Number.isFinite(u)) {
      setError('User and Vedika amounts must be valid numbers (vendor share is fixed at 0).');
      return;
    }
    const sum = roundMoney(u + ve + vn);
    if (Math.abs(sum - g) > 0.02) {
      setError(`User + Vedika + vendor must equal gross paid (${formatInr(g)}).`);
      return;
    }
    if (u < -0.01) {
      setError('User refund cannot be negative.');
      return;
    }
    const pen = penaltyAmount === '' ? NaN : Number(penaltyAmount);
    if (!Number.isFinite(pen)) {
      setError(`Enter ${penLabelShort.toLowerCase()} amount (use 0 if none), or set ${penLabelShort.toLowerCase()} %.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        status: 'COMPLETED',
        penaltyAmount: pen,
        userRefundAmount: roundMoney(u),
        vedikaShareAmount: roundMoney(ve),
        vendorShareAmount: roundMoney(vn),
      };
      if (adminNote) body.adminNote = adminNote;
      const res = await patchVendorManualRefundCase(row.caseId, body);
      if (!res.success || !res.data) throw new Error('Complete failed');
      onSaved();
      onClose();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : 'Complete failed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const markRejected = async () => {
    if (!row) return;
    setSaving(true);
    setError(null);
    try {
      const res = await patchVendorManualRefundCase(row.caseId, {
        status: 'REJECTED',
        adminNote: adminNote || undefined,
      });
      if (!res.success || !res.data) throw new Error('Reject failed');
      onSaved();
      onClose();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : 'Reject failed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const orderLedgerHref = row ? maOrderLedgerPath(row.serviceRefId, row.serviceType) : '#';
  const ambulanceBookingHref =
    row?.serviceType === 'AMBULANCE' && row.vendorId
      ? ambulanceBookingPath(row.vendorId, row.serviceRefId)
      : null;
  const isReadOnly = row && (row.status === 'COMPLETED' || row.status === 'REJECTED');
  const fullUserRefundPresetActive = Boolean(row && isFullUserRefundPresetRow(row));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-[88rem] flex flex-col max-h-[min(92dvh,calc(100vh-1rem))] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 flex-col gap-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Manual refund case</h2>
            {!loading && row && (
              <p className="mt-0.5 truncate font-mono text-xs text-gray-500 dark:text-gray-400">{row.caseId}</p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!loading && row && (
              <>
                <Link
                  to={orderLedgerHref}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <Wallet className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Order ledger</span>
                  <span className="sm:hidden">Ledger</span>
                </Link>
                {row.userId && (
                  <Link
                    to={userProfilePath(row.userId)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <User className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">User profile</span>
                    <span className="sm:hidden">User</span>
                  </Link>
                )}
                {ambulanceBookingHref && (
                  <Link
                    to={ambulanceBookingHref}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <span className="hidden sm:inline">Ambulance booking</span>
                    <span className="sm:hidden">Booking</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  </Link>
                )}
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <aside className="shrink-0 overflow-y-auto border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5 lg:w-[min(100%,20rem)] lg:max-w-xs lg:border-b-0 lg:border-r dark:lg:border-gray-700 xl:w-80">
            {loading && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {!loading && row && (
              <>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Status</span>
                    <div className="mt-1">
                      <CaseStatusBadge status={row.status} />
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Service</span>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {row.serviceType} · <span className="font-mono text-xs">{row.serviceRefId}</span>
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Trigger</span>
                    <p className="font-medium text-gray-900 dark:text-white">{row.triggerSource}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Gross paid</span>
                    <p className="text-xl font-bold tabular-nums text-gray-900 dark:text-white">
                      {formatInr(Number(row.grossPaidAmount))}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">User</span>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {row.user?.name || row.userId}
                      {row.user?.phone_number ? ` · ${row.user.phone_number}` : ''}
                    </p>
                    {row.userId && (
                      <Link
                        to={userProfilePath(row.userId)}
                        className="mt-1 inline-flex text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                      >
                        View user history →
                      </Link>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Vendor</span>
                    <p className="break-all font-medium text-gray-900 dark:text-white">
                      {row.vendor?.email || row.vendorId}
                    </p>
                  </div>
                  {row.cancellationReason && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Cancellation reason</span>
                      <p className="text-gray-900 dark:text-white">{row.cancellationReason}</p>
                    </div>
                  )}
                </div>
                {!isReadOnly && (
                  <div className="mt-4">
                    <RefundMathPanel math={row.refundMath} serviceType={row.serviceType} caseStatus={row.status} />
                  </div>
                )}
              </>
            )}
          </aside>

          <main className="min-h-0 min-w-0 flex-1 overflow-y-auto border-t border-gray-200 p-4 dark:border-gray-700 sm:p-5 lg:border-t-0 lg:border-l dark:lg:border-gray-700">
            {loading && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
              </div>
            )}

            {!loading && row && !isReadOnly && fullUserRefundPresetActive && (
              <div className="space-y-4">
                <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-900 dark:border-primary-900/50 dark:bg-primary-950/40 dark:text-primary-100">
                  <p className="font-semibold">Full user refund is saved on this case</p>
                  <p className="mt-2 text-primary-800 dark:text-primary-200">
                    User refund equals gross; Vedika and vendor shares are ₹0. Use <strong>Mark completed</strong> in the
                    footer to run refund processing and close the case.
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Gross booking amount
                  </p>
                  <p className="mt-1 text-3xl font-bold tabular-nums text-gray-900 dark:text-white sm:text-4xl">
                    {formatInr(gross)}
                  </p>
                </div>
                <label className="block text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Admin note</span>
                  <textarea
                    rows={3}
                    className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                  />
                </label>
              </div>
            )}

            {!loading && row && isReadOnly && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  This case is <strong className="text-gray-900 dark:text-white">{row.status}</strong>. Amounts are
                  locked; use links above for finance context.
                </p>
                {row.status === 'COMPLETED' && (
                  <>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Gross booking amount
                    </p>
                    <p className="text-3xl font-bold tabular-nums text-gray-900 dark:text-white sm:text-4xl">
                      {formatInr(Number(row.grossPaidAmount))}
                    </p>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{penLabel}</p>
                        <p className="mt-2 text-2xl font-bold tabular-nums text-gray-900 dark:text-white sm:text-3xl">
                          {formatInr(row.penaltyAmount != null ? Number(row.penaltyAmount) : null)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20">
                        <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">User refund</p>
                        <p className="mt-2 text-2xl font-bold tabular-nums text-gray-900 dark:text-white sm:text-3xl">
                          {formatInr(row.userRefundAmount != null ? Number(row.userRefundAmount) : null)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 dark:border-violet-900/50 dark:bg-violet-950/20 sm:col-span-2 xl:col-span-1">
                        <p className="text-sm font-semibold text-violet-800 dark:text-violet-300">Vedika / vendor</p>
                        <p className="mt-2 text-2xl font-bold tabular-nums text-gray-900 dark:text-white sm:text-3xl">
                          {formatInr(row.vedikaShareAmount != null ? Number(row.vedikaShareAmount) : null)}{' '}
                          <span className="text-lg font-medium text-gray-500 dark:text-gray-400">/</span>{' '}
                          {formatInr(row.vendorShareAmount != null ? Number(row.vendorShareAmount) : null)}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {!loading && row && !isReadOnly && !fullUserRefundPresetActive && (
          <section className="min-w-0">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Settlement amounts</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {penLabel}, user refund, and Vedika share must sum to gross paid.
                </p>
              </div>
              <div
                ref={settlementInfoRef}
                className="relative inline-flex"
                onMouseLeave={() => setSettlementInfoOpen(false)}
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                  aria-expanded={settlementInfoOpen}
                  aria-label="Settlement help"
                  onMouseEnter={() => setSettlementInfoOpen(true)}
                  onClick={() => setSettlementInfoOpen((o) => !o)}
                >
                  <Info className="h-4 w-4" />
                  How it works
                </button>
                {settlementInfoOpen && (
                  <div
                    role="tooltip"
                    className="absolute right-0 top-full z-30 mt-1 w-[min(calc(100vw-2rem),24rem)] rounded-lg border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-lg dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
                  >
                    <p>{settlementInfoText(row.serviceType)}</p>
                    {suggestedPenalty != null && Number.isFinite(suggestedPenalty) ? (
                      <p className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
                        Suggested {penLabelShort.toLowerCase()} (10% of gross): {formatInr(suggestedPenalty)} — set % to
                        ~10 or enter ₹.
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-6 rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/40 sm:px-6 sm:py-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Gross booking amount
              </p>
              <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                {formatInr(gross)}
              </p>
              {splitSum != null && (
                <p
                  className={`mt-2 flex flex-wrap items-center gap-2 text-sm ${
                    splitOk
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-amber-700 dark:text-amber-400'
                  }`}
                >
                  {splitOk ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 shrink-0" />
                  )}
                  <span>
                    User + Vedika + vendor = {formatInr(splitSum)}
                    {!splitOk && gross > 0 ? ` (must equal ${formatInr(gross)})` : null}
                  </span>
                </p>
              )}
            </div>

            {penaltyPctError && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
                <p>{penaltyPctError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
              <SettlementAmountCard
                label={penLabel}
                sublabel="% of gross — amount updates as you type"
                icon={Building2}
                border="border-amber-200 dark:border-amber-900/50"
                bg="bg-amber-50/60 dark:bg-amber-950/20"
                accent="text-amber-800 dark:text-amber-300"
                inputRing="focus:ring-amber-500/40"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:gap-6">
                  <label className="block shrink-0 md:w-28">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Percent</span>
                    <div className="relative mt-1.5">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-3 pr-10 text-lg font-semibold tabular-nums text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/40 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                        value={penaltyPct}
                        onChange={(e) => onPenaltyPctChange(e.target.value)}
                        placeholder="0"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        %
                      </span>
                    </div>
                    {penaltyPctDisplay != null && gross > 0 && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">of {formatInr(gross)}</p>
                    )}
                  </label>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Amount</p>
                    <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white sm:text-3xl xl:text-4xl">
                      {formatInr(penaltyAmtDisplay)}
                    </p>
                    <label className="mt-2 block">
                      <span className="sr-only">{penLabel} in rupees</span>
                      <div className="relative max-w-xs">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                          ₹
                        </span>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm tabular-nums text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500/40 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                          value={penaltyAmount}
                          onChange={(e) => onPenaltyAmountChange(e.target.value)}
                          placeholder="Edit ₹"
                          title={`${penLabel} in rupees (stays in sync with % when gross is known)`}
                        />
                      </div>
                    </label>
                  </div>
                </div>
              </SettlementAmountCard>

              <SettlementAmountCard
                label="User refund"
                sublabel="Gross minus Vedika share (from record)"
                icon={User}
                border="border-emerald-200 dark:border-emerald-900/50"
                bg="bg-emerald-50/60 dark:bg-emerald-950/20"
                accent="text-emerald-800 dark:text-emerald-300"
                inputRing="focus:ring-emerald-500/40"
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between md:gap-6">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Share of gross</p>
                    <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-900 dark:text-emerald-100 sm:text-3xl">
                      {userPctDisplay != null ? `${userPctDisplay}%` : '—'}
                    </p>
                  </div>
                  <div className="min-w-0 flex-1 md:text-right">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Refund to user</p>
                    <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white sm:text-3xl xl:text-4xl">
                      {formatInr(userAmt)}
                    </p>
                  </div>
                </div>
              </SettlementAmountCard>

              {vedikaAmt > 0.005 ? (
                <SettlementAmountCard
                  label="Vedika share"
                  sublabel="From order record (not editable here)"
                  icon={IndianRupee}
                  border="border-violet-200 dark:border-violet-900/50"
                  bg="bg-violet-50/60 dark:bg-violet-950/20"
                  accent="text-violet-800 dark:text-violet-300"
                  inputRing=""
                >
                  <p className="text-2xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white sm:text-3xl xl:text-4xl">
                    {formatInr(vedikaAmt)}
                  </p>
                </SettlementAmountCard>
              ) : null}
            </div>

            <label className="mt-6 block text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Admin note</span>
              <textarea
                rows={3}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
              />
            </label>
          </section>
            )}
          </main>
        </div>

        {!loading && row && !isReadOnly && (
          <footer className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40 sm:px-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <p className="order-2 max-w-2xl text-xs text-gray-500 dark:text-gray-400 lg:order-1">
                {fullUserRefundPresetActive ? (
                  <>
                    Full user refund is configured (user = gross, Vedika and vendor ₹0). Use{' '}
                    <strong>Mark completed</strong> to process the refund and close the case.
                  </>
                ) : (
                  <>
                    <strong>{penLabel}</strong> can be set as % of gross or ₹. User refund is gross minus Vedika (from
                    record); vendor share is fixed at ₹0. Totals must match gross ({formatInr(gross)}).
                  </>
                )}
              </p>
              <div className="order-1 flex flex-wrap items-center justify-end gap-2 lg:order-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void markRejected()}
                  className="rounded-lg border border-red-300 px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/30"
                >
                  Reject case
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void saveFields()}
                  className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-white disabled:opacity-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                >
                  Save draft
                </button>
                {row && isFullUserRefundPresetRow(row) ? (
                  <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-left dark:border-primary-900/50 dark:bg-primary-950/40">
                    <p className="text-sm font-semibold text-primary-900 dark:text-primary-100">Applied full refund</p>
                    <p className="text-xs text-primary-800 dark:text-primary-200">
                      {penLabel}:{' '}
                      {formatInr(
                        row.penaltyAmount != null && Number.isFinite(Number(row.penaltyAmount))
                          ? Number(row.penaltyAmount)
                          : 0,
                      )}
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void applyFullUserRefund()}
                    className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    Apply full user refund
                  </button>
                )}
                <button
                  type="button"
                  disabled={saving || !!penaltyPctError}
                  onClick={() => void markCompleted()}
                  className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  title="Server validates split and penalty vs gross"
                >
                  Mark completed
                </button>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

function serviceTypeLabel(code: string): string {
  const o = SERVICE_TYPE_OPTIONS.find((x) => x.value === code);
  return o?.label ?? code;
}

const VendorManualRefundCasesPage: React.FC = () => {
  const [rows, setRows] = useState<VendorManualRefundCaseRow[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [vendorRole, setVendorRole] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    status: '',
    serviceType: '',
    vendorRole: '',
    search: '',
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionMenu, setActionMenu] = useState<{ caseId: string; top: number; right: number } | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const caseIdFromUrl = new URLSearchParams(window.location.search).get('caseId')?.trim();
    if (!caseIdFromUrl) return;
    setSelectedId(caseIdFromUrl);
    const next = new URLSearchParams(window.location.search);
    next.delete('caseId');
    const qs = next.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`,
    );
  }, []);

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (actionMenuRef.current?.contains(t)) return;
      const trigger = (e.target as HTMLElement | null)?.closest('[data-refund-case-row-actions]');
      if (trigger?.getAttribute('data-refund-case-row-actions') === actionMenu.caseId) return;
      close();
    };
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [actionMenu]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchVendorManualRefundCases({
        page: pagination.page,
        limit: pagination.limit,
        status: appliedFilters.status || undefined,
        serviceType: appliedFilters.serviceType || undefined,
        vendorRole: appliedFilters.vendorRole || undefined,
        search: appliedFilters.search.trim() || undefined,
      });
      if (!res.success) throw new Error('Failed to load');
      setRows(res.data);
      setPagination(res.pagination);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, appliedFilters]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const actionMenuRow = actionMenu ? rows.find((x) => x.caseId === actionMenu.caseId) : undefined;

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manual refund cases</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Vendor cancellations queued for manual refund. Use <strong>Order ledger</strong> for finance breakdown (by
            service type). For ambulance cases, set <strong>ambulance agency penalty</strong> on gross paid before
            completing.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadList()}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="text-sm block min-w-[140px]">
          <span className="text-gray-600 dark:text-gray-300">Status</span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2 text-gray-900 dark:text-white"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm block min-w-[160px]">
          <span className="text-gray-600 dark:text-gray-300">Service type</span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2 text-gray-900 dark:text-white"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
          >
            {SERVICE_TYPE_OPTIONS.map((o) => (
              <option key={o.value || 'all-svc'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm block min-w-[180px]">
          <span className="text-gray-600 dark:text-gray-300">Vendor type</span>
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-2 text-gray-900 dark:text-white"
            value={vendorRole}
            onChange={(e) => setVendorRole(e.target.value)}
          >
            {VENDOR_ROLE_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'all-role'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm block flex-1 min-w-[200px]">
          <span className="text-gray-600 dark:text-gray-300">Search (case / ref)</span>
          <input
            className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-white"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="UUID or service ref"
          />
        </label>
        <button
          type="button"
          onClick={() => {
            setAppliedFilters({ status, serviceType, vendorRole, search: searchInput });
            setPagination((p) => ({ ...p, page: 1 }));
          }}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Apply filters
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-600 dark:text-gray-300">
              <th className="px-4 py-3 font-semibold">Created</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Vendor type</th>
              <th className="px-4 py-3 font-semibold">Service type</th>
              <th className="px-4 py-3 font-semibold">Ref</th>
              <th className="px-4 py-3 font-semibold text-right">Gross</th>
              <th className="px-4 py-3 font-semibold text-right">
                {appliedFilters.serviceType === 'AMBULANCE'
                  ? 'Agency penalty'
                  : appliedFilters.serviceType
                    ? 'Vendor penalty'
                    : 'Agency / vendor penalty'}
              </th>
              <th className="px-4 py-3 font-semibold">Trigger</th>
              <th className="px-4 py-3 w-[72px] text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin inline mr-2 align-middle" />
                  Loading…
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr
                  key={r.caseId}
                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50"
                >
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200 whitespace-nowrap">
                    {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <CaseStatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200">
                    {r.vendorTypeLabel ?? (r.vendorRole != null ? VENDOR_ROLE_NAMES[r.vendorRole] : '—')}
                  </td>
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{serviceTypeLabel(r.serviceType)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700 dark:text-gray-300 max-w-[140px] truncate">
                    {r.serviceRefId}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatInr(Number(r.grossPaidAmount))}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {r.penaltyAmount != null ? formatInr(Number(r.penaltyAmount)) : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.triggerSource}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      data-refund-case-row-actions={r.caseId}
                      aria-label="Row actions"
                      aria-expanded={actionMenu?.caseId === r.caseId}
                      aria-haspopup="menu"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setActionMenu((cur) =>
                          cur?.caseId === r.caseId
                            ? null
                            : { caseId: r.caseId, top: rect.bottom + 4, right: window.innerWidth - rect.right },
                        );
                      }}
                      className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No cases found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {actionMenu &&
        actionMenuRow &&
        createPortal(
          <div
            ref={actionMenuRef}
            role="presentation"
            className="fixed z-[300] min-w-[220px] rounded-lg border border-gray-200 bg-white py-1 text-left shadow-lg dark:border-gray-600 dark:bg-gray-800"
            style={{ top: actionMenu.top, right: actionMenu.right }}
          >
            <ul role="menu" className="m-0 list-none p-0">
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    setSelectedId(actionMenuRow.caseId);
                    setActionMenu(null);
                  }}
                >
                  <Eye className="h-4 w-4 shrink-0 opacity-70" />
                  View & edit case
                </button>
              </li>
              <li role="none">
                <Link
                  role="menuitem"
                  to={maOrderLedgerPath(actionMenuRow.serviceRefId, actionMenuRow.serviceType)}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setActionMenu(null)}
                >
                  <Wallet className="h-4 w-4 shrink-0 opacity-70" />
                  Order ledger
                </Link>
              </li>
              {actionMenuRow.userId ? (
                <li role="none">
                  <Link
                    role="menuitem"
                    to={userProfilePath(actionMenuRow.userId)}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setActionMenu(null)}
                  >
                    <User className="h-4 w-4 shrink-0 opacity-70" />
                    User profile
                  </Link>
                </li>
              ) : null}
              {actionMenuRow.serviceType === 'AMBULANCE' && actionMenuRow.vendorId ? (
                <li role="none">
                  <Link
                    role="menuitem"
                    to={ambulanceBookingPath(actionMenuRow.vendorId, actionMenuRow.serviceRefId)}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setActionMenu(null)}
                  >
                    <Truck className="h-4 w-4 shrink-0 opacity-70" />
                    Ambulance booking
                    <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 opacity-60" />
                  </Link>
                </li>
              ) : null}
            </ul>
          </div>,
          document.body,
        )}

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
              className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedId && (
        <CaseDetailModal
          caseId={selectedId}
          onClose={() => setSelectedId(null)}
          onSaved={() => void loadList()}
        />
      )}
    </div>
  );
};

export default VendorManualRefundCasesPage;
